import type { FC } from "react"

import type { MinimalToken } from "@/domains/core"
import { Button } from "@/components/button"
import { Dialog, DialogContent } from "@/components/dialog"
import { ShortMessageBox } from "@/domains/common/components/short-message-box"
import { WarningSquareIcon } from "@/domains/icons"
import { formatCurrency } from "@/domains/library"

interface CreateIncentiveAlertDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedToken: MinimalToken
  amount: string
  handleCreateIncentive: () => void
}

const CreateIncentiveAlertDialog: FC<CreateIncentiveAlertDialogProps> = ({
  isOpen,
  onClose,
  selectedToken,
  amount,
  handleCreateIncentive,
}) => {
  const handleConfirm = () => {
    onClose()
    handleCreateIncentive()
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(value: boolean) => {
        if (!value) {
          onClose()
        }
      }}
    >
      <DialogContent
        title="Are you absolutely sure?"
        className="flex w-full flex-col gap-6 border border-p400-light bg-p400 p-6 text-t100 sm:max-w-md"
      >
        <div className="text-sm text-t200">
          You are incentivizing {formatCurrency(amount)} {selectedToken.symbol}. This is an advanced feature that is
          normally used by protocols. If you are trying to deposit liquidity in a pool then please use the liquidity
          page instead.
        </div>

        <ShortMessageBox icon={<WarningSquareIcon size={20} />} variant="error" size="sm">
          Incentives deposited here are not reimbursable.
        </ShortMessageBox>

        <div className="relative mb-2 mt-2 h-0 w-full overflow-visible rounded-md border border-solid border-p400-light after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md  after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']" />

        <div className="flex justify-end gap-3">
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Create incentive
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default CreateIncentiveAlertDialog
